<template>
  <div class="p-4 flex-shrink-0 w-full group block relative main"
       :class="{
          'bg-gray-700': isRunning,
          'bg-red-700': status == 'failed',
          'bg-yellow-700': status == 'cancelled',
          'bg-green-700': status == 'finished',
       }">
    <div class="wrapper">
      <div>
        <component :is="statusIcon" class="w-6 h-6 text-white" />
      </div>
      <div class="ml-3">
        <p class="header">
          {{ header }}
        </p>
        <p class="details" v-if="isRunning">
          Jobs processed: {{ processedJobs }}/{{ importData.total_jobs }}
        </p>
        <p class="details">
          {{ updateLabel }}: {{ lastUpdate }}
        </p>
      </div>
    </div>
    <div class="mt-4 tooltip hidden" v-if="isRunning">
      <p class="details">
        Total jobs: {{ importData.total_jobs }}<br/>
        Processed jobs: {{ processedJobs }}
      </p>

      <button type="button"
              :disabled="loading"
              @click="cancel"
              class="w-full block mt-4 text-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import {
  RefreshIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/vue/outline'
import formatRelative from 'date-fns/formatRelative'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import { startCase } from 'lodash'
import axios from 'axios'
import Eta from '@gofreddo/eta'

export default {
  props: {
    batch: Object,
  },

  data() {
    return {
      importData: {
        ...this.batch,
        last_update: new Date(),
      },
      loading: false,
    }
  },

  mounted() {
    this.queueStatusUpdate()
  },

  methods: {
    formatDate(date) {
      return date
        ? formatRelative(new Date(date), new Date())
        : null
    },

    async updateStatus() {
      if (!this.isRunning) {
        return;
      }

      this.loading = true

      const response = await axios.get(`/ajax/import/${this.importData.id}`)
      const { data } = response.data

      this.importData = {
        ...data,
        last_update: new Date(),
      }
      this.loading = false

      this.queueStatusUpdate()
    },

    queueStatusUpdate() {
      setTimeout(() => {
        this.updateStatus()
      }, 5000)
    },

    cancel() {
      if (confirm('Do you want to cancel the import?')) {
        this.loading = true
        axios.post(`/ajax/import/${this.importData.id}/cancel`)
          .then(() => this.updateStatus())
      }
    },
  },

  computed: {
    isRunning() {
      return !['finished', 'warnings', 'failed', 'canceled'].includes(this.status)
    },

    progress() {
      if (! this.stats) {
        return 0
      }

      return this.stats.percentComplete
    },

    processedJobs() {
      if (! this.importData.total_jobs) {
        return 0
      }

      return this.importData.total_finished_jobs
    },

    lastUpdate() {
      return this.formatDate(this.importData.updated_at);
    },

    status() {
      return this.importData.phase;
    },

    header() {
      if (this.isRunning) {
        return this.importData.phase_label
      }

      switch (this.status) {
        case 'warnings':
          return `Builds import finished (w/ ${this.status})`
        default:
          return `Builds import ${this.status}`
      }
    },

    updateLabel() {
      if (this.isRunning) {
        return 'Last update'
      }

      switch (this.status) {
        case 'warnings':
          return 'Finished'

        default:
          return startCase(this.status)
      }
    },

    statusIcon() {
      switch (this.status) {
        case 'finished':
        case 'warnings':
          return CheckCircleIcon
        case 'failed':
          return ExclamationCircleIcon
        case 'canceled':
          return XCircleIcon

        default:
          return RefreshIcon
      }
    },

    timeToFinish() {
      if (! this.stats?.eta || ! +this.stats?.eta) {
        return null
      }

      return formatDistanceToNow(new Date(this.stats.eta))
    },

    stats() {
      if (!this.isRunning || ! this.importData.created_at) {
        return null
      }

      const progress = new Eta({
        total: this.importData.total_jobs,
        count: this.importData.total_finished_jobs,
        startTime: new Date(this.importData.created_at * 1000),
      })

      return progress.stats()
    }
  },
}
</script>

<style scoped>
.wrapper {
  @apply flex items-center;
}
.header {
  @apply text-sm font-medium text-white;
}
.details {
  @apply text-xs font-medium text-gray-300 group-hover:text-gray-200;
}
.main:hover {
  .tooltip {
    @apply block;
  }
}
</style>
